import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Activity, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Spinner, Empty } from "@/components/Common";

const ZONE: Record<string, string> = { testa: "Testa", fronte_occhi: "Fronte e occhi", gola: "Gola", petto: "Petto", addome: "Addome", braccia_mani: "Braccia e mani", gambe_piedi: "Gambe e piedi", nuca: "Testa (dietro)", collo: "Collo", schiena_alta: "Schiena alta", schiena_bassa: "Schiena bassa" };
const MOOD: Record<string, string> = { bene: "Sto bene", ansia: "Sono in ansia", giu: "Mi sento giù", solo: "Mi sento solo" };
const STARTED: Record<string, string> = { adesso: "iniziata adesso", oggi: "da oggi", giorni: "da qualche giorno", settimane: "da settimane" };
const SENS_LABEL: Record<string, string> = { pressione: "Pressione", dolore: "Dolore", formicolio: "Formicolio", tensione: "Tensione", tremore: "Tremore", palpitazioni: "Palpitazioni", calore: "Calore", altro: "Altro" };
const EMPTY_TXT: Record<string, string> = { "": "Il diario è vuoto. Puoi scrivere da Oggi o annotare una sensazione da “Ascolta il tuo corpo”.", note: "Nessun pensiero salvato. Puoi scriverne uno da Oggi.", body: "Nessuna sensazione annotata. Prova “Ascolta il tuo corpo”." };

function Entry({ e, onDelete }: any) {
  const b = e.body;
  return (
    <article className="row items-start" data-testid="journal-entry">
      <span className="w-9 h-9 rounded-full tone-cyan flex items-center justify-center shrink-0">{b ? <Activity size={16} /> : <NotebookPen size={16} />}</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-ink/60">{new Date(e.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}{e.mood ? ` · ${MOOD[e.mood]}` : ""}</div>
        {b && <div className="font-semibold mt-0.5">{ZONE[b.zone]}: {b.sensations.map((s: string) => (s === "altro" && b.other ? b.other : SENS_LABEL[s] || s)).join(", ")} · {b.intensity}/10 · {STARTED[b.started]}</div>}
        {e.text && <p className="text-sm text-ink/80 mt-1 whitespace-pre-wrap break-words">{e.text}</p>}
      </div>
      <button className="icon-btn" aria-label="Elimina voce" onClick={onDelete} data-testid="journal-delete-btn"><Trash2 size={16} /></button>
    </article>
  );
}

function Recurrences({ data }: { data: any[] }) {
  const counts: Record<string, number> = {};
  data.filter((e) => e.body).forEach((e) => { counts[e.body.zone] = (counts[e.body.zone] || 0) + 1; });
  const top = Object.entries(counts).filter(([, n]) => n > 1).sort((a, b) => b[1] - a[1]).slice(0, 3);
  if (!top.length) return null;
  return <p className="text-sm text-ink/72" data-testid="journal-recurrences">Zone annotate più volte: {top.map(([z, n]) => `${ZONE[z]} (${n})`).join(", ")}. Se qualcosa si ripete, può essere utile parlarne con il medico.</p>;
}

export default function Diario() {
  const qc = useQueryClient();
  const [kind, setKind] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["journal", kind], queryFn: () => api.get("/journal", { params: kind ? { kind } : {} }).then((r) => r.data) });
  const refresh = () => ["journal", "score"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const del = async (id: string) => { try { await api.delete(`/journal/${id}`); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const delAll = async () => {
    if (!window.confirm("Eliminare tutte le voci del diario? Non si può annullare.")) return;
    try { await api.delete("/journal"); refresh(); toast.success("Diario svuotato"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="diario-page">
      <PageHeader kicker="Visibile solo a te" title="Il mio diario" back="/" />
      <div className="px-5 md:px-10 max-w-2xl space-y-5 pb-12">
        <div className="flex flex-wrap items-center gap-2">
          <div className="seg" role="tablist">{[["", "Tutto"], ["note", "Pensieri"], ["body", "Corpo"]].map(([k, l]) => <button key={k} role="tab" aria-selected={kind === k} onClick={() => setKind(k)} className={`seg-item ${kind === k ? "seg-active" : ""}`} data-testid={`journal-tab-${k || "all"}`}>{l}</button>)}</div>
          <Link to="/corpo" className="btn btn-ghost ml-auto" data-testid="journal-body-link"><Activity size={16} />Ascolta il corpo</Link>
        </div>
        {isLoading ? <Spinner /> : !data.length ? <Empty testId="journal-empty">{EMPTY_TXT[kind]}</Empty> : (
          <>
            <Recurrences data={data} />
            <div className="surface p-1" data-testid="journal-list">{data.map((e: any) => <Entry key={e.id} e={e} onDelete={() => del(e.id)} />)}</div>
            {!kind && <button className="btn btn-danger" onClick={delAll} data-testid="journal-delete-all-btn"><Trash2 size={15} />Elimina tutto il diario</button>}
          </>
        )}
      </div>
    </div>
  );
}
